import * as React from 'react';

import {Stack} from 'shells/layout/stack/stack';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {useGetProductCategories} from '../../categories/graphql/hooks/use-get-product-categories';
import {useGetProductsSidebarCounts} from '../../categories/graphql/hooks/use-get-products-sidebar-counts';
import {useGetMarkets} from '../../core/select/hooks';

import {SettingsSidebar} from '../settings-sidebar';

import {
    useGetProducts,
    useCreateProduct,
    useUpdateProduct,
    useDeleteProduct,
    useUndeleteProduct,
} from './graphql/hooks';
import {mapSearchParamToAPIValue, mapParamToArchivedAPIValue} from './helpers';
import {ProductsSidebarSection} from './products-sidebar-section';
import {ProductsTable} from './products-table';

import styles from './products-wrapper.module.css';

export function ProductsWrapper() {
    const searchParams = new URLSearchParams(window.location.search);
    const archived = mapParamToArchivedAPIValue(searchParams.get('archived'));
    const selectedCategoryId = mapSearchParamToAPIValue(searchParams.get('category'));

    const {products, isLoading, hasNextPage, fetchMore} = useGetProducts(
        selectedCategoryId,
        archived
    );

    const {markets, isLoading: isMarketsLoading} = useGetMarkets();
    const {createProduct, isLoading: isCreatingProduct} = useCreateProduct(
        selectedCategoryId,
        archived
    );
    const {updateProduct, isLoading: isUpdatingProduct} = useUpdateProduct(
        selectedCategoryId,
        archived
    );
    const {deleteProduct} = useDeleteProduct(selectedCategoryId, archived);
    const {undeleteProduct} = useUndeleteProduct();
    const {productCategories} = useGetProductCategories();
    const {productsSidebarCounts} = useGetProductsSidebarCounts();

    if ((isLoading && !products) || isMarketsLoading || !markets) {
        return <LoadingPage />;
    }

    const selectedCategory =
        productCategories &&
        productCategories.find((category) => category.id === selectedCategoryId);
    const categoryreportLink = selectedCategory && selectedCategory.reportLink;

    return (
        <Stack isHorizontal={true} spacing={16}>
            <div className={styles['sidebar-container']}>
                <Stack spacing={16}>
                    <ProductsSidebarSection
                        productCategories={productCategories}
                        isViewingArchived={archived}
                        selectedCategoryId={selectedCategoryId}
                        numProducts={
                            (productsSidebarCounts && productsSidebarCounts.numProducts) || 0
                        }
                        numUncategorizedProducts={
                            (productsSidebarCounts &&
                                productsSidebarCounts.numUncategorizedProducts) ||
                            0
                        }
                        numDeletedProducts={
                            (productsSidebarCounts && productsSidebarCounts.numDeletedProducts) || 0
                        }
                    />
                    <SettingsSidebar glossaryKey='/setup/product' />
                </Stack>
            </div>
            <div className={styles['table-wrapper']}>
                {isLoading ? (
                    <div className={styles['loading-container']}>
                        <LoadingPage />
                    </div>
                ) : (
                    <ProductsTable
                        products={products}
                        markets={markets}
                        hasNextPage={hasNextPage}
                        fetchMore={fetchMore}
                        onProductCreate={createProduct}
                        onProductUpdate={updateProduct}
                        onProductDelete={deleteProduct}
                        onProductUndelete={undeleteProduct}
                        isSaving={isCreatingProduct || isUpdatingProduct}
                        selectedCategory={selectedCategory}
                        categoryreportLink={categoryreportLink}
                        isViewingUncategorizedProducts={selectedCategoryId === null}
                        isViewingArchived={Boolean(archived)}
                    />
                )}
            </div>
        </Stack>
    );
}
